import { useContext } from 'react';
import useSWR from 'swr';
import { UseSWRMutationResult } from '../../common/types/swrTypes';
import { buildQueryString, deleteToApi, getFromApi, postToApi, putToApi } from '../../common/utils/apiHelper';
import { ConfigContext } from '../configuration/ConfigContext';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { CreatePinboardMessageRequest } from './models/CreatePinboardMessageRequest';
import { GetPinboardMessageResponse } from './models/GetPinboardMessageResponse';
import { GetPinboardMessageStatisticsResponse } from './models/GetPinboardMessageStatisticsResponse';
import { GetPinboardMessagesPagedResponse } from './models/GetPinboardMessagesResponse';
import { UpdatePinboardMessageRequest } from './models/UpdatePinboardMessageRequest';

type UsePinboardMessagesResult = UseSWRMutationResult<GetPinboardMessagesPagedResponse, 'response'>;

export const usePinboardMessages = (): UsePinboardMessagesResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();

    const url = `${apiBaseUrl}/pinboardmessages${buildQueryString({ propertyAssociationId: currentPropertyAssociationId, areaId: currentArea?.propertyAssociationAreaId })}`;

    const { data, error, isLoading, mutate } = useSWR<GetPinboardMessagesPagedResponse>(url);

    return {
        response: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

type UsePinboardMessageResult = UseSWRMutationResult<GetPinboardMessageResponse, 'pinboardMessage'>;

export const usePinboardMessage = (pinboardMessageId: string | undefined): UsePinboardMessageResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = pinboardMessageId && currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/pinboardmessages/${pinboardMessageId}` : null;

    const { data, error, isLoading, mutate } = useSWR<GetPinboardMessageResponse>(url);

    return {
        pinboardMessage: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

type UsePinboardMessageStatisticsResult = UseSWRMutationResult<GetPinboardMessageStatisticsResponse, 'statistics'>;

export const usePinboardMessageStatistics = (pinboardMessageId: string | undefined): UsePinboardMessageStatisticsResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = pinboardMessageId && currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/pinboardmessages/${pinboardMessageId}/statistics` : null;

    const { data, error, isLoading, mutate } = useSWR<GetPinboardMessageStatisticsResponse>(url);

    return {
        statistics: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

export const getStatistics = (
    apiBaseUrl: string,
    propertyAssociationId: string,
    pinboardMessageId: string,
    onSuccess: (data: GetPinboardMessageStatisticsResponse) => void,
    onFail: () => void,
): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/pinboardmessages/${pinboardMessageId}/statistics`, onSuccess, onFail);
};

export const setSettings = (apiBaseUrl: string, propertyAssociationId: string, pinboardMessageId: string, usersCanRespond: boolean, onSuccess: () => void, onFail: () => void): void => {
    const settings = { usersCanRespond };
    postToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/pinboardmessages/${pinboardMessageId}/settings`, settings, onSuccess, onFail);
};

export const createPinboardMessage = (apiBaseUrl: string, data: CreatePinboardMessageRequest, onSuccess: (id: string) => void, onFail: () => void): void => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('text', data.text);
    formData.append('isPinned', JSON.stringify(data.isPinned));
    formData.append('usersCanRespond', JSON.stringify(data.usersCanRespond));

    if (data.targetAssociations) {
        formData.append('targetAssociationsJson', JSON.stringify(data.targetAssociations));
    }

    if (data.files) {
        for (let i = 0; i < data.files.length; i++) {
            formData.append('files', data.files[i]);
        }
    }

    postToApi(`${apiBaseUrl}/pinboardmessages`, formData, onSuccess, onFail);
};

export const updatePinboardMessage = (
    apiBaseUrl: string,
    propertyAssociationId: string,
    pinboardMessageId: string,
    data: UpdatePinboardMessageRequest,
    onSuccess: () => void,
    onFail: () => void,
): void => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('text', data.text);
    formData.append('isPinned', JSON.stringify(data.isPinned));
    formData.append('usersCanRespond', JSON.stringify(data.usersCanRespond));

    if (data.filePaths) {
        for (let i = 0; i < data.filePaths.length; i++) {
            formData.append('filePaths', data.filePaths[i]);
        }
    }

    if (data.files) {
        for (let i = 0; i < data.files.length; i++) {
            formData.append('files', data.files[i]);
        }
    }

    putToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/pinboardmessages/${pinboardMessageId}`, formData, onSuccess, onFail);
};

export const deletePinboardMessage = (apiBaseUrl: string, propertyAssociationId: string, pinboardMessageId: string, onSuccess: () => void, onFail: () => void): void => {
    deleteToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/pinboardmessages/${pinboardMessageId}`, onSuccess, onFail);
};
